const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://api.smarthivesms.com";
const SESSION_KEY = "smarthivesms_landing_session_id";
const UTM_KEY = "smarthivesms_landing_utm_params";

export interface EventProperties {
  [key: string]: string | number | boolean | null | undefined;
}

const getSessionId = (): string => {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = "lp_sess_" + Math.random().toString(36).substring(2, 11) + "_" + Date.now();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return "session_lp_fallback_" + Date.now();
  }
};

export const captureUtmParams = (): Record<string, string> => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get("utm_source");
    const utmMedium = urlParams.get("utm_medium");
    const utmCampaign = urlParams.get("utm_campaign");
    const utmTerm = urlParams.get("utm_term");
    const ref = urlParams.get("ref") || urlParams.get("referrer");

    if (utmSource || utmMedium || utmCampaign || utmTerm || ref) {
      const utmObj = {
        utm_source: utmSource || ref || "",
        utm_medium: utmMedium || "",
        utm_campaign: utmCampaign || "",
        utm_term: utmTerm || "",
      };
      sessionStorage.setItem(UTM_KEY, JSON.stringify(utmObj));
      return utmObj;
    }

    const cached = sessionStorage.getItem(UTM_KEY);
    return cached ? JSON.parse(cached) : {};
  } catch {
    return {};
  }
};

export const trackEvent = (
  eventName: string,
  properties: EventProperties = {},
  eventType: string = "engagement"
) => {
  try {
    const utm = captureUtmParams();
    const sessionId = getSessionId();

    const payload = {
      eventType,
      eventName,
      sessionId,
      properties: {
        ...properties,
        ...utm,
        screen_width: window.innerWidth,
        screen_height: window.innerHeight,
      },
      utmSource: utm.utm_source || null,
      utmMedium: utm.utm_medium || null,
      utmCampaign: utm.utm_campaign || null,
      referrer: document.referrer || null,
      path: window.location.pathname + window.location.search,
    };

    const targetUrl = `${API_BASE_URL}/api/analytics/track`;

    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      navigator.sendBeacon(targetUrl, blob);
    } else {
      fetch(targetUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    }
  } catch (err) {
    // Fail silently in production
  }
};

export const trackLandingPageView = (path?: string) => {
  trackEvent("landing_page_view", { title: document.title, path: path || window.location.pathname }, "pageview");
};

export const trackCtaClick = (ctaName: string, destinationUrl?: string, locationOnPage?: string) => {
  trackEvent("cta_clicked", { ctaName, destinationUrl, locationOnPage }, "funnel");
};

export const trackPricingSelection = (planName: string, billingCycle: string, estimatedVolume?: number) => {
  trackEvent("pricing_plan_selected", { planName, billingCycle, estimatedVolume }, "funnel");
};

export default {
  trackEvent,
  trackLandingPageView,
  trackCtaClick,
  trackPricingSelection,
  captureUtmParams,
};
